import { httpClient } from '../lib/http-client';
import { Ticket, TicketResposta, Tag, TicketRelacionado, ComentarioInterno, BuscaAvancadaFiltros, BuscaAvancadaResponse } from '../types';
import { TIMEOUTS } from '../config/constants';

export interface TicketFilters {
  limite?: number;
  offset?: number;
  status?: string;
  prioridade?: string;
  unidade?: string;
  departamento?: string;
  usuario?: string;
  assunto?: string;
  tem_anexos?: string;
  tem_notificacoes?: string;
  eh_favorito?: string;
  eh_mencionado?: string;
  eh_duplicado?: string;
  data_criacao_inicio?: string;
  data_criacao_fim?: string;
  data_atualizacao_inicio?: string;
  data_atualizacao_fim?: string;
  ordenar_por?: string;
  ordenar_direcao?: 'asc' | 'desc';
  tags?: string[];
  exibir_fechados?: string;
}

export class TicketsService {
  /**
   * Lista de tickets com paginação e filtros do backend
   */
  static async getTickets(params?: TicketFilters): Promise<BuscaAvancadaResponse> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v.toString()));
          } else {
            searchParams.append(key, value.toString());
          }
        }
      });
    }

    const query = searchParams.toString();
    return httpClient.get<BuscaAvancadaResponse>(`/api/tickets${query ? `?${query}` : ''}`);
  }

  // Mantido apenas por compatibilidade, usa a paginação padrão
  static async searchTickets(termo: string): Promise<BuscaAvancadaResponse> {
    return this.getTickets();
  }

  static async getTicketsRecebidos(departamentoId: number): Promise<Ticket[]> {
    return httpClient.get<Ticket[]>(`/api/tickets/departamento/${departamentoId}`);
  }

  static async getTicketsCriados(usuarioId: number): Promise<Ticket[]> {
    return httpClient.get<Ticket[]>(`/api/tickets/usuario/${usuarioId}`);
  }

  static async getTicket(id: number): Promise<Ticket> {
    try {
      const response = await httpClient.get<Ticket>(`/api/tickets/${id}?include=respostas`);
      if (response.respostas !== undefined) {
        return response;
      }
      return httpClient.get<Ticket>(`/api/tickets/${id}`);
    } catch (error) {
      return httpClient.get<Ticket>(`/api/tickets/${id}`);
    }
  }

  static async createTicket(data: Partial<Ticket>): Promise<Ticket> {
    return httpClient.post<Ticket>('/api/tickets', data);
  }

  static async updateTicket(id: number, data: Partial<Ticket>): Promise<Ticket> {
    return httpClient.put<Ticket>(`/api/tickets/${id}`, data);
  }

  static async deleteTicket(id: number): Promise<{ success: boolean; message?: string }> {
    return httpClient.delete<{ success: boolean; message?: string }>(`/api/tickets/${id}`);
  }

  static async createTicketResponse(
    ticketId: number,
    conteudo: string,
    anexos?: File[]
  ): Promise<TicketResposta> {
    if (anexos && anexos.length > 0) {
      const formData = new FormData();
      formData.append('conteudo', conteudo);
      anexos.forEach((file, index) => {
        formData.append(`anexos[${index}]`, file);
      });

      return httpClient.upload<TicketResposta>(
        `/api/tickets/${ticketId}/respostas`,
        formData,
        TIMEOUTS.UPLOAD
      );
    } else {
      return httpClient.post<TicketResposta>(`/api/tickets/${ticketId}/respostas`, {
        conteudo,
      });
    }
  }

  static async updateTicketResponse(
    respostaId: number,
    conteudo: string
  ): Promise<{ success: boolean; message?: string }> {
    return httpClient.put<{ success: boolean; message?: string }>(
      `/api/ticket-respostas/${respostaId}`,
      { conteudo }
    );
  }

  static async deleteTicketResponse(
    respostaId: number
  ): Promise<{ success: boolean; message?: string }> {
    return httpClient.delete<{ success: boolean; message?: string }>(
      `/api/ticket-respostas/${respostaId}`
    );
  }

  static async uploadTicketFiles(
    ticketId: number,
    formData: FormData
  ): Promise<{ success: boolean }> {
    try {
      return await httpClient.upload<{ success: boolean }>(
        `/api/tickets/${ticketId}/anexos`,
        formData,
        TIMEOUTS.UPLOAD
      );
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Erro ao enviar anexos');
    }
  }

  static async getTicketsSlaEstourado(): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    return httpClient.get<{ success: boolean; data?: any; error?: string }>(
      '/api/tickets/sla-estourado'
    );
  }

  static async getMeusTickets(params?: TicketFilters): Promise<BuscaAvancadaResponse> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v.toString()));
          } else {
            searchParams.append(key, value.toString());
          }
        }
      });
    }

    const query = searchParams.toString();
    return httpClient.get<BuscaAvancadaResponse>(`/api/meus-tickets${query ? `?${query}` : ''}`);
  }

  static async alterarStatusTicket(id: number, statusId: number): Promise<Ticket> {
    return httpClient.patch<Ticket>(`/api/tickets/${id}/status`, { status_id: statusId });
  }

  static async iniciarAtendimento(ticketId: number): Promise<{ ticket: Ticket; mensagem: string }> {
    return httpClient.post<{ ticket: Ticket; mensagem: string }>(
      `/api/tickets/${ticketId}/iniciar-atendimento`
    );
  }

  static async fecharTicket(ticketId: number): Promise<{ success: boolean; message: string }> {
    return httpClient.put<{ success: boolean; message: string }>(
      `/api/tickets/${ticketId}/fechar`
    );
  }

  static async transferirTicket(
    ticketId: number,
    departamentoId: number
  ): Promise<{
    success: boolean;
    message: string;
    departamento_anterior: string | null;
    departamento_novo: string;
    ticket: Ticket;
  }> {
    return httpClient.put<{
      success: boolean;
      message: string;
      departamento_anterior: string | null;
      departamento_novo: string;
      ticket: Ticket;
    }>(`/api/tickets/${ticketId}/transferir`, { departamento_id: departamentoId });
  }

  static async downloadTicketAnexo(id: number): Promise<Blob> {
    const response = await httpClient.getInstance().get(`/api/tickets/anexos/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Busca avançada
  static async buscaAvancada(filtros: BuscaAvancadaFiltros): Promise<BuscaAvancadaResponse> {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.append(key, value.toString());
        }
      }
    });
    
    return httpClient.get<BuscaAvancadaResponse>(`/api/tickets/busca-avancada?${params.toString()}`);
  }

  static async getTagsDoTicket(ticketId: number): Promise<Tag[]> {
    return httpClient.get<Tag[]>(`/api/tickets/${ticketId}/tags`);
  }

  static async adicionarTagsAoTicket(ticketId: number, tagIds: number[]): Promise<{ success: boolean; tags: Tag[]; tags_adicionadas: number[] }> {
    return httpClient.post<{ success: boolean; tags: Tag[]; tags_adicionadas: number[] }>(
      `/api/tickets/${ticketId}/tags`,
      { tags: tagIds }
    );
  }

  static async removerTagsDoTicket(ticketId: number, tagIds: number[]): Promise<{ success: boolean; tags: Tag[]; tags_removidas: number }> {
    const response = await httpClient.getInstance().delete<{ success: boolean; tags: Tag[]; tags_removidas: number }>(
      `/api/tickets/${ticketId}/tags`,
      { data: { tags: tagIds } }
    );
    return response.data;
  }

  static async getTicketsRelacionados(ticketId: number): Promise<TicketRelacionado[]> {
    return httpClient.get<TicketRelacionado[]>(`/api/tickets/${ticketId}/relacionados`);
  }

  static async vincularTicket(ticketId: number, ticketRelacionadoId: number, tipoRelacao?: string): Promise<TicketRelacionado> {
    return httpClient.post<TicketRelacionado>(
      `/api/tickets/${ticketId}/relacionados`,
      { ticket_relacionado_id: ticketRelacionadoId, tipo_relacao: tipoRelacao || 'relacionado' }
    );
  }

  static async desvincularTicket(ticketId: number, relacionamentoId: number): Promise<{ success: boolean; message: string }> {
    return httpClient.delete<{ success: boolean; message: string }>(
      `/api/tickets/${ticketId}/relacionados/${relacionamentoId}`
    );
  }

  static async getComentariosInternos(ticketId: number): Promise<ComentarioInterno[]> {
    return httpClient.get<ComentarioInterno[]>(`/api/tickets/${ticketId}/comentarios-internos`);
  }

  static async adicionarComentarioInterno(ticketId: number, comentario: string): Promise<ComentarioInterno> {
    return httpClient.post<ComentarioInterno>(
      `/api/tickets/${ticketId}/comentarios-internos`,
      { comentario }
    );
  }

  static async atualizarComentarioInterno(ticketId: number, comentarioId: number, comentario: string): Promise<ComentarioInterno> {
    return httpClient.put<ComentarioInterno>(
      `/api/tickets/${ticketId}/comentarios-internos/${comentarioId}`,
      { comentario }
    );
  }

  static async deletarComentarioInterno(ticketId: number, comentarioId: number): Promise<{ success: boolean; message: string }> {
    return httpClient.delete<{ success: boolean; message: string }>(
      `/api/tickets/${ticketId}/comentarios-internos/${comentarioId}`
    );
  }

  static async verificarDuplicados(data: {
    departamentos: Array<{ departamento_id: number; assunto: string }>;
    descricao: string;
  }): Promise<{
    tem_duplicados: boolean;
    duplicados: Array<{
      ticket_id: number;
      assunto: string;
      departamento_id: number;
      departamento_nome: string;
      status_id: number;
      status_nome: string;
      usuario_nome: string;
      created_at: string;
    }>;
    total: number;
  }> {
    return httpClient.post<{
      tem_duplicados: boolean;
      duplicados: Array<{
        ticket_id: number;
        assunto: string;
        departamento_id: number;
        departamento_nome: string;
        status_id: number;
        status_nome: string;
        usuario_nome: string;
        created_at: string;
      }>;
      total: number;
    }>('/api/tickets/verificar-duplicados', data);
  }
}

